import { computed, reactive, watch } from 'vue';

export const REPRODUCED_OPTIONS = [
  'GitLab.com',
  'GitLab Self-Managed',
  'GitLab Dedicated',
  'GitLab Dedicated for Gov',
].map(option => `reproduced on ${option}`);

export function useLabelState(props) {
  const typeHierarchy = Object.fromEntries(
    props.labelConfig.types.map((type) => {
      const prefix = `${type.split('::')[1]}::`;
      const options = props.labelConfig.subtypes
        .filter(subtype => subtype.startsWith(prefix))
        .map(subtype => subtype.replace(prefix, ''));

      return [type, { prefix, options }];
    }),
  );

  const subtypePrefixes = props.labelConfig.types.map(type => `${type.split('::')[1]}::`);
  const areaOptions = props.labelConfig.areas;
  const reproducedOptions = REPRODUCED_OPTIONS;
  const allCategories = props.groups.flatMap(group => group.categories);
  const groupOptions = props.groups.map(group => group.label).sort();

  const labels = props.labels;
  const existing = {
    type: labels.find(label => label.startsWith('type::')),
    subtype: labels.find(label => subtypePrefixes.some(prefix => label.startsWith(prefix))),
    group: labels.find(label => groupOptions.includes(label)),
    category: labels.find(label => allCategories.includes(label)),
    areas: labels.filter(label => areaOptions.includes(label)),
    reproduced: labels.filter(label => reproducedOptions.includes(label)),
  };

  const updatable = reactive({
    type: existing.type,
    subtype: existing.subtype,
    group: existing.group,
    category: existing.category,
    areas: Object.fromEntries(areaOptions.map(area => [area, labels.includes(area)])),
    reproduced: Object.fromEntries(reproducedOptions.map(option => [option, labels.includes(option)])),
  });

  const subtypeOptions = Object.entries(typeHierarchy).flatMap(
    ([typeKey, { prefix, options }]) => options.map(option => ({ value: `${prefix}${option}`, typeKey })),
  );

  const availableTypeOptions = computed(() => {
    const subtype = existing.subtype || updatable.subtype;
    if (existing.type || subtype) {
      return [existing.type || `type::${subtype.split('::')[0]}`];
    }

    return Object.keys(typeHierarchy).sort();
  });

  const availableSubtypeOptions = computed(() => subtypeOptions
    .filter(option => !updatable.type || option.typeKey === updatable.type)
    .map(option => option.value)
    .sort());

  const categoryOptions = computed(() => {
    if (!updatable.group) {
      return [];
    }

    const group = props.groups.find(({ label }) => label === updatable.group);

    return (group?.categories || []).filter(Boolean).sort();
  });

  const typeLocked = computed(() => !!existing.type || !!existing.subtype || !!updatable.subtype);

  const payload = computed(() => ({
    type: updatable.type,
    subtype: updatable.subtype,
    group: updatable.group,
    category: updatable.category,
    areas: Object.entries(updatable.areas).filter(([, selected]) => selected).map(([area]) => area),
    reproduced: Object.entries(updatable.reproduced).filter(([, selected]) => selected).map(([option]) => option),
  }));

  watch(
    () => updatable.subtype,
    (subtype) => {
      if (subtype && !updatable.type) {
        updatable.type = `type::${subtype.split('::')[0]}`;
      }
    },
    { immediate: true },
  );

  watch(() => updatable.group, (next, previous) => {
    if (previous && next !== previous) {
      updatable.category = undefined;
    }
  });

  watch(() => updatable.type, (next, previous) => {
    if (previous && next !== previous) {
      updatable.subtype = undefined;
    }
  });

  return {
    existing,
    updatable,
    areaOptions,
    reproducedOptions,
    groupOptions,
    availableTypeOptions,
    availableSubtypeOptions,
    categoryOptions,
    typeLocked,
    payload,
  };
}
